import type { ImprovementRequestFilter as PilabRequestFilter } from './api/pilab-requests.js';
import type { OrgIssueFilter } from './api/org-issues.js';
import type {
  KpiSummaryParams,
  KpiTrendParams,
  KpiWeeklyTrendParams,
  AceMonthlyTrendParams,
  KpiPointProgressParams,
  BugDetailParams,
  BugBreakdownParams,
  BugBreakdownDetailsParams,
  BugSeverityParams,
  BugEscapeTimingParams,
  BugEscapeTimingDetailsParams,
  ReleaseDetailParams,
  DevelopPointDetailParams,
  IpointTrendParams,
  QaEvaluationsParams,
  MemberListParams,
  MemberTrendParams,
  MemberDetailParams,
  MemberGithubActivityParams,
  MemberGithubActivityIndexParams,
  EngineerEvaluationsParams,
  EngineerEvaluationDetailParams,
  CcCommandMatrixParams,
  CcEventsByNameParams,
  CcEventsQueryParams,
  CcEventsTimeseriesParams,
  CcUsageQueryParams,
  CcUsageTimeseriesParams,
  CcUsageTimeseriesByGroupParams,
  McpUsageQueryParams,
  McpUsageTimeseriesParams,
} from '../types/api.js';
import type { CrossDivPmoDivision } from '@cortex/core-types';
import type { DesignSearchParams } from '../types/design-search.js';
import type { TableFilter } from '../types/db-dictionary.js';
import type { KnowledgeWikiFilter } from '../types/knowledge-wiki.js';
import type { PmoFlowSummaryParams, PmoFlowTrendParams } from '../types/pmo-flow.js';
import type { SreOpenBugTrendParams } from '../types/sre-open-bug-trend.js';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business TanStack Queryキー定義。auth/kpi/pmoFlow/pmoDelay/qa/accounts/members/projects/dbDictionary/ccUsage/mcpUsageの階層的クエリキーでキャッシュ管理
 * @graph-connects none
 */
export const queryKeys = {
  auth: {
    all: ['auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
  },
  profile: {
    all: ['profile'] as const,
    detail: () => [...queryKeys.profile.all, 'detail'] as const,
  },
  kpi: {
    all: ['kpi'] as const,
    summary: (params?: KpiSummaryParams) => [...queryKeys.kpi.all, 'summary', params] as const,
    trend: (params?: KpiTrendParams) => [...queryKeys.kpi.all, 'trend', params] as const,
    weeklyTrend: (params?: KpiWeeklyTrendParams) =>
      [...queryKeys.kpi.all, 'weeklyTrend', params] as const,
    aceMonthlyTrend: (params?: AceMonthlyTrendParams) =>
      [...queryKeys.kpi.all, 'aceMonthlyTrend', params] as const,
    pointProgress: (params?: KpiPointProgressParams) =>
      [...queryKeys.kpi.all, 'pointProgress', params] as const,
    budgetFiscalYearStatus: (params?: { now?: string; mockFiscalYears?: string }) =>
      [...queryKeys.kpi.all, 'budgetFiscalYearStatus', params] as const,
    monthlyBudgets: () => [...queryKeys.kpi.all, 'monthlyBudgets'] as const,
    bugs: (params?: BugDetailParams) => [...queryKeys.kpi.all, 'bugs', params] as const,
    bugBreakdown: (params?: BugBreakdownParams) =>
      [...queryKeys.kpi.all, 'bugBreakdown', params] as const,
    bugBreakdownDetails: (params?: BugBreakdownDetailsParams) =>
      [...queryKeys.kpi.all, 'bugBreakdownDetails', params] as const,
    bugSeverity: (params?: BugSeverityParams) =>
      [...queryKeys.kpi.all, 'bugSeverity', params] as const,
    bugEscapeTiming: (params?: BugEscapeTimingParams) =>
      [...queryKeys.kpi.all, 'bugEscapeTiming', params] as const,
    bugEscapeTimingDetails: (params?: BugEscapeTimingDetailsParams) =>
      [...queryKeys.kpi.all, 'bugEscapeTimingDetails', params] as const,
    bugDetectionDays: (params?: BugDetailParams) =>
      [...queryKeys.kpi.all, 'bugDetectionDays', params] as const,
    releases: (params?: ReleaseDetailParams) => [...queryKeys.kpi.all, 'releases', params] as const,
    developPoints: (params?: DevelopPointDetailParams) =>
      [...queryKeys.kpi.all, 'developPoints', params] as const,
  },
  sreOpenBugTrend: {
    all: ['sreOpenBugTrend'] as const,
    trend: (params: SreOpenBugTrendParams) =>
      [...queryKeys.sreOpenBugTrend.all, 'trend', params] as const,
  },
  pmoFlow: {
    all: ['pmoFlow'] as const,
    summary: (params?: PmoFlowSummaryParams) =>
      [...queryKeys.pmoFlow.all, 'summary', params] as const,
    trend: (params?: PmoFlowTrendParams) => [...queryKeys.pmoFlow.all, 'trend', params] as const,
    supplyRate: () => [...queryKeys.pmoFlow.all, 'supplyRate'] as const,
    supplyRateProjects: (stage: string) =>
      [...queryKeys.pmoFlow.all, 'supplyRateProjects', stage] as const,
    scaleLeadTime: (month: string | null, exclude: string) =>
      [...queryKeys.pmoFlow.all, 'scaleLeadTime', month, exclude] as const,
    scaleTailProjects: (scope: string, month: string, percentile: number, exclude: string) =>
      [...queryKeys.pmoFlow.all, 'scaleTailProjects', scope, month, percentile, exclude] as const,
  },
  pmoDelay: {
    all: ['pmoDelay'] as const,
    view: (quarter?: string) => [...queryKeys.pmoDelay.all, 'view', quarter ?? 'current'] as const,
  },
  qa: {
    all: ['qa'] as const,
    evaluations: (params?: QaEvaluationsParams) =>
      [...queryKeys.qa.all, 'evaluations', params] as const,
    evaluationDetail: (id: string) => [...queryKeys.qa.all, 'evaluation', id] as const,
  },
  accounts: {
    all: ['accounts'] as const,
    list: () => [...queryKeys.accounts.all, 'list'] as const,
    search: (query: string) => [...queryKeys.accounts.all, 'search', query] as const,
  },
  members: {
    all: ['members'] as const,
    list: (params?: MemberListParams) => [...queryKeys.members.all, 'list', params] as const,
    detail: (email: string) => [...queryKeys.members.all, 'detail', email] as const,
    trend: (email: string, params?: MemberTrendParams) =>
      [...queryKeys.members.all, 'trend', email, params] as const,
    developPoints: (email: string, params?: MemberDetailParams) =>
      [...queryKeys.members.all, 'developPoints', email, params] as const,
    releases: (email: string, params?: MemberDetailParams) =>
      [...queryKeys.members.all, 'releases', email, params] as const,
    projects: (email: string) => [...queryKeys.members.all, 'projects', email] as const,
    requirementProjects: (email: string) =>
      [...queryKeys.members.all, 'requirementProjects', email] as const,
    projectHistory: (email: string, limit: number) =>
      [...queryKeys.members.all, 'projectHistory', email, limit] as const,
    quarterlyKpi: (email: string, quarter?: string) =>
      [...queryKeys.members.all, 'quarterlyKpi', email, quarter] as const,
    wipPoints: (email: string) => [...queryKeys.members.all, 'wipPoints', email] as const,
    openPoints: (email: string) => [...queryKeys.members.all, 'openPoints', email] as const,
    developPointTargets: (email: string) =>
      [...queryKeys.members.all, 'developPointTargets', email] as const,
    githubSummary: (login: string, params?: MemberGithubActivityParams) =>
      [...queryKeys.members.all, 'githubSummary', login, params] as const,
    githubTimeseries: (login: string, params?: MemberGithubActivityParams) =>
      [...queryKeys.members.all, 'githubTimeseries', login, params] as const,
    githubByRepo: (login: string, params?: MemberGithubActivityParams) =>
      [...queryKeys.members.all, 'githubByRepo', login, params] as const,
    githubPartners: (login: string, params?: MemberGithubActivityParams) =>
      [...queryKeys.members.all, 'githubPartners', login, params] as const,
    githubActivities: (login: string, params?: MemberGithubActivityParams) =>
      [...queryKeys.members.all, 'githubActivities', login, params] as const,
    githubPr: (login: string, id: string) =>
      [...queryKeys.members.all, 'githubPr', login, id] as const,
    githubIndex: (params?: MemberGithubActivityIndexParams) =>
      [...queryKeys.members.all, 'githubIndex', params] as const,
    githubAggregateSummary: (logins: string[], params?: MemberGithubActivityParams) =>
      [...queryKeys.members.all, 'githubAggregateSummary', logins, params] as const,
    githubAggregateTimeseries: (logins: string[], params?: MemberGithubActivityParams) =>
      [...queryKeys.members.all, 'githubAggregateTimeseries', logins, params] as const,
    githubAggregateByRepo: (logins: string[], params?: MemberGithubActivityParams) =>
      [...queryKeys.members.all, 'githubAggregateByRepo', logins, params] as const,
    githubAggregatePartners: (logins: string[], params?: MemberGithubActivityParams) =>
      [...queryKeys.members.all, 'githubAggregatePartners', logins, params] as const,
    githubAggregateActivities: (logins: string[], params?: MemberGithubActivityParams) =>
      [...queryKeys.members.all, 'githubAggregateActivities', logins, params] as const,
  },
  projects: {
    all: ['projects'] as const,
    list: (division?: CrossDivPmoDivision) =>
      [...queryKeys.projects.all, 'list', division ?? null] as const,
    children: (parentId: number) => [...queryKeys.projects.all, 'children', parentId] as const,
    pmoOverdue: () => [...queryKeys.projects.all, 'pmoOverdue'] as const,
    pmoUpcoming: (daysAhead: number) =>
      [...queryKeys.projects.all, 'pmoUpcoming', daysAhead] as const,
    pmoReleaseLeaks: () => [...queryKeys.projects.all, 'pmoReleaseLeaks'] as const,
    pmoAssignLeaks: () => [...queryKeys.projects.all, 'pmoAssignLeaks'] as const,
    assignmentHistory: () => [...queryKeys.projects.all, 'assignmentHistory'] as const,
    myProjectsBoard: () => [...queryKeys.projects.all, 'myProjectsBoard'] as const,
    lightTaskPool: () => [...queryKeys.projects.all, 'lightTaskPool'] as const,
    assignedLightTasks: () => [...queryKeys.projects.all, 'assignedLightTasks'] as const,
    performanceAgg: (mode: 'dev' | 'design', startDate: string, endDate: string) =>
      [...queryKeys.projects.all, 'performanceAgg', mode, startDate, endDate] as const,
    reviewerResources: () => [...queryKeys.projects.all, 'reviewerResources'] as const,
    detail: (pjKey: string) => [...queryKeys.projects.all, 'detail', pjKey] as const,
    integratedProduct: (pjKey: string) =>
      [...queryKeys.projects.all, 'integratedProduct', pjKey] as const,
    deliverableFolders: (pjKey: string) =>
      [...queryKeys.projects.all, 'deliverableFolders', pjKey] as const,
  },
  meetings: {
    all: ['meetings'] as const,
    /**
     * tags 省略時は案件の全一覧キャッシュに共通するプレフィックスを返す
     * （作成・タグ更新後の invalidateQueries はこの形で呼び、タグ絞り込み中の
     * キャッシュも含めて再取得させる）。tags 指定時はソート済みの配列を
     * キーに含める（選択順序違いで別キャッシュに分裂させないため）。
     */
    list: (pjKey: string, tags?: readonly string[]) =>
      tags === undefined
        ? ([...queryKeys.meetings.all, 'list', pjKey] as const)
        : ([...queryKeys.meetings.all, 'list', pjKey, [...tags].sort()] as const),
    detail: (pjKey: string, meetingId: string) =>
      [...queryKeys.meetings.all, 'detail', pjKey, meetingId] as const,
  },
  specRequirements: {
    all: ['specRequirements'] as const,
    list: () => [...queryKeys.specRequirements.all, 'list'] as const,
    /**
     * 言語ごとに別のキーにする。言語を切り替えたら読み直す必要がある。
     */
    detail: (pjKey: string, locale: string) =>
      [...queryKeys.specRequirements.all, 'detail', pjKey, locale] as const,
    /**
     * 案件の全言語ぶんを畳む prefix。**保存のあとはこちらを無効化する。**
     * 保存は原文も訳も動かすので、今見ている言語だけ読み直すと他の言語が古いまま残る。
     */
    detailAll: (pjKey: string) => [...queryKeys.specRequirements.all, 'detail', pjKey] as const,
    nextStep: (pjKey: string) => [...queryKeys.specRequirements.all, 'nextStep', pjKey] as const,
    plReview: (pjKey: string) => [...queryKeys.specRequirements.all, 'plReview', pjKey] as const,
    format: (artifact: string) => [...queryKeys.specRequirements.all, 'format', artifact] as const,
    /** レビュー 1 件の出来事（開いたときだけ引く） */
    reviewEvents: (pjKey: string, targetId: string) =>
      [...queryKeys.specRequirements.all, 'reviewEvents', pjKey, targetId] as const,
  },
  basicDesign: {
    all: ['basicDesign'] as const,
    /** 節の定義はデプロイでしか変わらないが、言語ごとに文言が違う */
    artifacts: (lang: string) => [...queryKeys.basicDesign.all, 'artifacts', lang] as const,
    format: (artifact: string, lang: string) =>
      [...queryKeys.basicDesign.all, 'format', artifact, lang] as const,
    targets: (key: string, lang: string) =>
      [...queryKeys.basicDesign.all, 'targets', key, lang] as const,
    /** 一覧の全言語ぶんを畳む prefix。保存のあとはこちらを無効化する */
    listAll: () => [...queryKeys.basicDesign.all, 'list'] as const,
    list: (lang: string) => [...queryKeys.basicDesign.listAll(), lang] as const,
    detail: (bdKey: string) => [...queryKeys.basicDesign.all, 'detail', bdKey] as const,
    nextStepAll: (bdKey: string) => [...queryKeys.basicDesign.all, 'nextStep', bdKey] as const,
    nextStep: (bdKey: string, lang: string) =>
      [...queryKeys.basicDesign.nextStepAll(bdKey), lang] as const,
    diagramsAll: (bdKey: string) => [...queryKeys.basicDesign.all, 'diagrams', bdKey] as const,
    diagrams: (bdKey: string, lang: string) =>
      [...queryKeys.basicDesign.diagramsAll(bdKey), lang] as const,
    revisions: (bdKey: string, artifact: string) =>
      [...queryKeys.basicDesign.all, 'revisions', bdKey, artifact] as const,
    revision: (bdKey: string, artifact: string, version: number) =>
      [...queryKeys.basicDesign.revisions(bdKey, artifact), version] as const,
    graphAll: (bdKey: string) => [...queryKeys.basicDesign.all, 'graph', bdKey] as const,
    graph: (bdKey: string, lang: string) =>
      [...queryKeys.basicDesign.graphAll(bdKey), lang] as const,
    reviewAll: (bdKey: string) => [...queryKeys.basicDesign.all, 'review', bdKey] as const,
    review: (bdKey: string, lang: string) =>
      [...queryKeys.basicDesign.reviewAll(bdKey), lang] as const,
    reviewers: (query: string) => [...queryKeys.basicDesign.all, 'reviewers', query] as const,
    notes: (bdKey: string) => [...queryKeys.basicDesign.all, 'notes', bdKey] as const,
  },
  dbDictionary: {
    all: ['dbDictionary'] as const,
    schemas: () => [...queryKeys.dbDictionary.all, 'schemas'] as const,
    tables: (schema: string, filter?: TableFilter) =>
      [...queryKeys.dbDictionary.all, 'tables', schema, filter] as const,
    tableDetail: (schema: string, table: string) =>
      [...queryKeys.dbDictionary.all, 'tableDetail', schema, table] as const,
  },
  knowledgeWiki: {
    all: ['knowledgeWiki'] as const,
    list: (filter?: KnowledgeWikiFilter) =>
      [...queryKeys.knowledgeWiki.all, 'list', filter] as const,
    detail: (id: string, locale?: string) =>
      [...queryKeys.knowledgeWiki.all, 'detail', id, locale] as const,
  },
  engineerEvaluation: {
    all: ['engineerEvaluation'] as const,
    evaluations: (params?: EngineerEvaluationsParams) =>
      [...queryKeys.engineerEvaluation.all, 'evaluations', params] as const,
    detail: (params: EngineerEvaluationDetailParams) =>
      [...queryKeys.engineerEvaluation.all, 'detail', params] as const,
    mySummary: () => [...queryKeys.engineerEvaluation.all, 'mySummary'] as const,
  },
  projectFeedback: {
    all: ['projectFeedback'] as const,
    list: () => [...queryKeys.projectFeedback.all, 'list'] as const,
    mine: () => [...queryKeys.projectFeedback.all, 'mine'] as const,
    detail: (feedbackId: string) =>
      [...queryKeys.projectFeedback.all, 'detail', feedbackId] as const,
    revisions: (feedbackId: string) =>
      [...queryKeys.projectFeedback.all, 'revisions', feedbackId] as const,
  },
  bugEvaluation: {
    all: ['bugEvaluation'] as const,
    list: (params?: { startDate?: string; endDate?: string; reviewStatus?: string }) =>
      [...queryKeys.bugEvaluation.all, 'list', params] as const,
    detail: (issueKey: string) => [...queryKeys.bugEvaluation.all, 'detail', issueKey] as const,
    aiDrafts: () => [...queryKeys.bugEvaluation.all, 'aiDrafts'] as const,
    implementerCandidates: (issueKey: string) =>
      [...queryKeys.bugEvaluation.all, 'implementerCandidates', issueKey] as const,
    causeProjectCandidates: (issueKey: string) =>
      [...queryKeys.bugEvaluation.all, 'causeProjectCandidates', issueKey] as const,
    ipointTrend: (params?: IpointTrendParams) =>
      [...queryKeys.bugEvaluation.all, 'ipointTrend', params] as const,
  },
  qaResponsibility: {
    all: ['qaResponsibility'] as const,
    list: (params?: { startDate?: string; endDate?: string; qaReviewStatus?: string }) =>
      [...queryKeys.qaResponsibility.all, 'list', params] as const,
    detail: (issueKey: string) => [...queryKeys.qaResponsibility.all, 'detail', issueKey] as const,
  },
  ccUsage: {
    all: ['ccUsage'] as const,
    summary: (params?: CcUsageQueryParams) =>
      [...queryKeys.ccUsage.all, 'summary', params] as const,
    timeseries: (params?: CcUsageTimeseriesParams) =>
      [...queryKeys.ccUsage.all, 'timeseries', params] as const,
    timeseriesByGroup: (params?: CcUsageTimeseriesByGroupParams) =>
      [...queryKeys.ccUsage.all, 'timeseriesByGroup', params] as const,
    maxRangeTimeseriesByGroup: (params?: {
      scope?: CcUsageTimeseriesByGroupParams['scope'];
      team?: string;
      email?: string;
      groupBy?: CcUsageTimeseriesByGroupParams['groupBy'];
      excludeTeams?: string[];
      excludeNicknames?: string[];
    }) => [...queryKeys.ccUsage.all, 'maxRangeTimeseriesByGroup', params] as const,
    byUser: (params?: CcUsageQueryParams) => [...queryKeys.ccUsage.all, 'byUser', params] as const,
    byRepository: (params?: CcUsageQueryParams) =>
      [...queryKeys.ccUsage.all, 'byRepository', params] as const,
    byTeam: (params?: CcUsageQueryParams) => [...queryKeys.ccUsage.all, 'byTeam', params] as const,
    teams: (params?: Pick<CcUsageQueryParams, 'range' | 'from' | 'to'>) =>
      [...queryKeys.ccUsage.all, 'teams', params] as const,
    members: (params?: Pick<CcUsageQueryParams, 'range' | 'from' | 'to'>) =>
      [...queryKeys.ccUsage.all, 'members', params] as const,
  },
  ccEvents: {
    all: ['ccEvents'] as const,
    summary: (params?: CcEventsQueryParams) =>
      [...queryKeys.ccEvents.all, 'summary', params] as const,
    byName: (params?: CcEventsByNameParams) =>
      [...queryKeys.ccEvents.all, 'byName', params] as const,
    byUser: (params?: CcEventsQueryParams) =>
      [...queryKeys.ccEvents.all, 'byUser', params] as const,
    byProject: (params?: CcEventsQueryParams) =>
      [...queryKeys.ccEvents.all, 'byProject', params] as const,
    timeseries: (params?: CcEventsTimeseriesParams) =>
      [...queryKeys.ccEvents.all, 'timeseries', params] as const,
    commandMatrixMembers: (params?: CcCommandMatrixParams) =>
      [...queryKeys.ccEvents.all, 'commandMatrixMembers', params] as const,
    commandMatrixProjects: (params?: CcCommandMatrixParams) =>
      [...queryKeys.ccEvents.all, 'commandMatrixProjects', params] as const,
  },
  mcpUsage: {
    all: ['mcpUsage'] as const,
    summary: (params?: McpUsageQueryParams) =>
      [...queryKeys.mcpUsage.all, 'summary', params] as const,
    timeseries: (params?: McpUsageTimeseriesParams) =>
      [...queryKeys.mcpUsage.all, 'timeseries', params] as const,
    byServer: (params?: McpUsageQueryParams) =>
      [...queryKeys.mcpUsage.all, 'byServer', params] as const,
    byTool: (params?: McpUsageQueryParams) =>
      [...queryKeys.mcpUsage.all, 'byTool', params] as const,
    byUser: (params?: McpUsageQueryParams) =>
      [...queryKeys.mcpUsage.all, 'byUser', params] as const,
    teams: (params?: Pick<McpUsageQueryParams, 'range' | 'from' | 'to'>) =>
      [...queryKeys.mcpUsage.all, 'teams', params] as const,
  },
  pilabRequests: {
    all: ['pilabRequests'] as const,
    list: (filter: PilabRequestFilter) => [...queryKeys.pilabRequests.all, 'list', filter] as const,
    guideline: () => [...queryKeys.pilabRequests.all, 'guideline'] as const,
  },
  cuttysark: {
    all: ['cuttysark'] as const,
    list: () => [...queryKeys.cuttysark.all, 'list'] as const,
    live: (sessionId: string) => [...queryKeys.cuttysark.all, 'live', sessionId] as const,
    myBallot: (sessionId: string) => [...queryKeys.cuttysark.all, 'myBallot', sessionId] as const,
    myComments: (sessionId: string) =>
      [...queryKeys.cuttysark.all, 'myComments', sessionId] as const,
    ballots: (sessionId: string) => [...queryKeys.cuttysark.all, 'ballots', sessionId] as const,
  },
  orgIssues: {
    all: ['orgIssues'] as const,
    boards: () => [...queryKeys.orgIssues.all, 'boards'] as const,
    myActions: () => [...queryKeys.orgIssues.all, 'myActions'] as const,
    members: (boardId: string) => [...queryKeys.orgIssues.all, 'members', boardId] as const,
    issues: (boardId: string, filter: OrgIssueFilter) =>
      [...queryKeys.orgIssues.all, 'issues', boardId, filter] as const,
    issue: (boardId: string, issueId: string) =>
      [...queryKeys.orgIssues.all, 'issue', boardId, issueId] as const,
  },
  teamMeetings: {
    all: ['teamMeetings'] as const,
    series: () => [...queryKeys.teamMeetings.all, 'series'] as const,
    seriesDetail: (seriesId: string) =>
      [...queryKeys.teamMeetings.all, 'seriesDetail', seriesId] as const,
    meeting: (seriesId: string, meetingId: string) =>
      [...queryKeys.teamMeetings.all, 'meeting', seriesId, meetingId] as const,
    autoBlocks: (seriesId: string, meetingId: string) =>
      [...queryKeys.teamMeetings.all, 'autoBlocks', seriesId, meetingId] as const,
  },
  teamAssignments: {
    all: ['teamAssignments'] as const,
    list: () => [...queryKeys.teamAssignments.all, 'list'] as const,
  },
  designSearch: {
    all: ['designSearch'] as const,
    search: (params: DesignSearchParams) =>
      [...queryKeys.designSearch.all, 'search', params] as const,
  },
} as const;
